/**
 * UB#10 fix (Phase 2B Sub-dispatch 4) — catalog-level flag banner derivation.
 *
 * Pure function extracted from ReviewTable.tsx (class2Banner.ts precedent) so
 * the catalog-level flag-render surface is unit-testable without mounting the
 * full ReviewTable component.
 *
 * THE GAP (BUG-001 Phase 2A audit §A.2.2 / multi-file §13.5.0): 704 catalog-
 * level flag rows across ~10 flag types (image_orphan_sku_not_in_catalog,
 * catalog_anchor_demoted, image_unmatched, source_recall_drift, grouping_*,
 * title_derived, etc.) are written with variant_id IS NULL and have NO
 * merchant-visible surface — only the encrypted-ZIP types render (a separate
 * red banner). A flag that fires must change behavior OR surface in the UI
 * (CLAUDE.md "Failsafe-must-act-not-just-observe", LOCKED 2026-05-29).
 *
 * THE FIX (D1, Halt #5): surface all non-ZIP catalog-level flags at
 * info-severity in a dismissible banner. ZIP encryption flags keep their own
 * red banner (handled separately in ReviewTable). The merchant message + label
 * resolution happens at render-time via FLAG_LABELS / DRAWER_MESSAGES;
 * source_recall_drift renders as merchant-action-recommended copy per D2.
 *
 * D3: quality_signals is an admin-only telemetry TABLE, not a flags row — the
 * /catalog-flags endpoint queries the `flags` table only, so telemetry never
 * reaches this surface by construction. No filtering needed for it here.
 */

/** Encrypted-ZIP flag types render in their own red banner — excluded here. */
export const ZIP_BANNER_FLAG_TYPES: ReadonlySet<string> = new Set([
  'zip_password_required',
  'zip_zipcrypto_unsupported',
  'zip_aes_encryption_unsupported',
]);

export interface CatalogFlagBannerItem {
  flag_type: string;
  /** Number of catalog-level rows of this flag_type (deduped into one line). */
  count: number;
  /** First non-empty stored message for this flag_type, if any. */
  message: string | null;
}

export interface CatalogFlagsBannerState {
  /** True when at least one non-ZIP catalog-level flag exists AND not dismissed. */
  show: boolean;
  /** Deduped, alphabetically-sorted (by flag_type) banner items. */
  items: CatalogFlagBannerItem[];
}

/**
 * Derive the catalog-level info banner state.
 *
 *   show=true requires:
 *     - at least one unresolved, non-ZIP catalog-level flag
 *     - dismissed is false
 *
 * Rows are deduped by flag_type (counts summed, first non-empty message kept)
 * and sorted alphabetically by flag_type for deterministic render order.
 * Malformed payloads (null/undefined/non-array, entries without flag_type)
 * coerce to an empty result.
 */
export function deriveCatalogFlagsBanner(
  catalogFlags: unknown,
  dismissed: boolean,
): CatalogFlagsBannerState {
  if (!Array.isArray(catalogFlags)) {
    return { show: false, items: [] };
  }

  const byType = new Map<string, CatalogFlagBannerItem>();
  for (const f of catalogFlags) {
    if (!f || typeof f !== 'object') continue;
    const flagType = (f as any).flag_type;
    if (typeof flagType !== 'string' || flagType.length === 0) continue;
    if ((f as any).resolved === true) continue;
    if (ZIP_BANNER_FLAG_TYPES.has(flagType)) continue;

    const existing = byType.get(flagType);
    const msg = typeof (f as any).message === 'string' && (f as any).message.trim().length > 0
      ? (f as any).message
      : null;
    if (existing) {
      existing.count += 1;
      if (!existing.message && msg) existing.message = msg;
    } else {
      byType.set(flagType, { flag_type: flagType, count: 1, message: msg });
    }
  }

  const items = Array.from(byType.values()).sort((a, b) =>
    a.flag_type.localeCompare(b.flag_type),
  );

  return {
    show: items.length > 0 && !dismissed,
    items,
  };
}
